import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Schedule as TimeIcon,
  Clear as ClearIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { format, isSameDay } from 'date-fns';
import {
  THEME_RADIUS,
  THEME_COLORS,
  THEME_GRADIENTS,
  THEME_SHADOWS,
} from '../constants/theme';


// Start Time Panel - Simplified with only dropdown selectors
const StartTimePanel = React.memo(({ startTime, onStartTimeChange }) => {

  // Generate hour options (1-12)
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  // Generate minute options (0, 15, 30, 45)
  const minutes = [0, 15, 30, 45];
  const periods = ['AM', 'PM'];

  const formatTime = (timeObj) => {
    const hour = timeObj.hour.toString().padStart(2, '0');
    const minute = timeObj.minute.toString().padStart(2, '0');
    return `${hour}:${minute} ${timeObj.period}`;
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: THEME_COLORS.text.secondary }}>
        Start Time
      </Typography>
      
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1, sm: 1.5 },
        p: { xs: 1, sm: 1.5 },
        border: `2px solid ${THEME_COLORS.primary.main}20`,
        borderRadius: THEME_RADIUS.medium,
        background: 'rgba(255, 255, 255, 0.9)',
        minHeight: { xs: 'auto', sm: 'auto' },
        '&:hover': {
          borderColor: THEME_COLORS.primary.main,
          background: 'rgba(255, 255, 255, 1)',
        }
      }}>
        {/* Time Selectors Container */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-end', 
          gap: { xs: 0.25, sm: 0.5 },
          justifyContent: 'center',
          flexWrap: 'nowrap',
          width: '100%'
        }}>
          {/* Hour Selector */}
          <Box sx={{ 
            flex: 1, 
            minWidth: { xs: 60, sm: 70 },
            maxWidth: { xs: 80, sm: 90 }
          }}>
            <Typography variant="caption" sx={{ 
              color: THEME_COLORS.text.secondary, 
              mb: 0.25, 
              display: 'block',
              fontSize: { xs: '0.65rem', sm: '0.7rem' },
              textAlign: 'center',
              fontWeight: 600
            }}>
              Hour
            </Typography>
            <Select
              value={startTime.hour}
              onChange={(e) => onStartTimeChange({ ...startTime, hour: e.target.value })}
              size="small"
              fullWidth
              sx={{
                '& .MuiSelect-select': {
                  textAlign: 'center',
                  fontWeight: 600,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  py: { xs: 0.5, sm: 0.5 }
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 200,
                    '& .MuiMenuItem-root': {
                      justifyContent: 'center',
                      minHeight: 40,
                    }
                  }
                }
              }}
            >
              {hours.map(hour => (
                <MenuItem key={hour} value={hour}>
                  {hour.toString().padStart(2, '0')}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Typography variant="h6" sx={{ 
            color: THEME_COLORS.text.secondary, 
            mx: { xs: 0.1, sm: 0.25 },
            alignSelf: 'flex-end',
            mb: 0.5,
            fontSize: { xs: '1.1rem', sm: '1.3rem' },
            fontWeight: 300
          }}>
            :
          </Typography>

          {/* Minute Selector */}
          <Box sx={{ 
            flex: 1, 
            minWidth: { xs: 60, sm: 70 },
            maxWidth: { xs: 80, sm: 90 }
          }}>
            <Typography variant="caption" sx={{ 
              color: THEME_COLORS.text.secondary, 
              mb: 0.25, 
              display: 'block',
              fontSize: { xs: '0.65rem', sm: '0.7rem' },
              textAlign: 'center',
              fontWeight: 600
            }}>
              Minute
            </Typography>
            <Select
              value={startTime.minute}
              onChange={(e) => onStartTimeChange({ ...startTime, minute: e.target.value })}
              size="small"
              fullWidth
              sx={{
                '& .MuiSelect-select': {
                  textAlign: 'center',
                  fontWeight: 600,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  py: { xs: 1, sm: 0.5 }
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 200,
                    '& .MuiMenuItem-root': {
                      justifyContent: 'center',
                      minHeight: 40,
                    }
                  }
                }
              }}
            >
              {minutes.map(minute => (
                <MenuItem key={minute} value={minute}>
                  {minute.toString().padStart(2, '0')}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Period Selector */}
          <Box sx={{ 
            flex: 1, 
            minWidth: { xs: 60, sm: 70 },
            maxWidth: { xs: 80, sm: 90 }
          }}>
            <Typography variant="caption" sx={{ 
              color: THEME_COLORS.text.secondary, 
              mb: 0.25, 
              display: 'block',
              fontSize: { xs: '0.65rem', sm: '0.7rem' },
              textAlign: 'center',
              fontWeight: 600
            }}>
              Period
            </Typography>
            <Select
              value={startTime.period}
              onChange={(e) => onStartTimeChange({ ...startTime, period: e.target.value })}
              size="small"
              fullWidth
              sx={{
                '& .MuiSelect-select': {
                  textAlign: 'center',
                  fontWeight: 600,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  py: { xs: 1, sm: 0.5 }
                }
              }}
            >
              {periods.map(period => (
                <MenuItem key={period} value={period}>
                  {period}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* Time Display */}
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 0.5, sm: 1 },
          mt: { xs: 0.5, sm: 0.5 }
        }}>
          <Box sx={{ 
            p: { xs: 1, sm: 1.25 }, 
            background: THEME_GRADIENTS.primary,
            borderRadius: THEME_RADIUS.small,
            minWidth: { xs: 100, sm: 100 },
            textAlign: 'center',
            boxShadow: THEME_SHADOWS.small,
            flex: 1,
            maxWidth: { xs: 140, sm: 120 }
          }}>
            <Typography variant="body2" sx={{ 
              color: 'white', 
              fontWeight: 600,
              fontSize: { xs: '0.9rem', sm: '0.875rem' }
            }}>
              {formatTime(startTime)}
            </Typography>
          </Box>

          <IconButton
            size="small"
            sx={{ 
              color: THEME_COLORS.text.secondary,
              p: { xs: 0.5, sm: 1 },
              '&:hover': {
                color: THEME_COLORS.primary.main,
              }
            }}
          >
            <AccessTimeIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
});

// End Time Panel - Simplified with only dropdown selectors
const EndTimePanel = React.memo(({ endTime, onEndTimeChange }) => {

  // Generate hour options (1-12)
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  // Generate minute options (0, 15, 30, 45)
  const minutes = [0, 15, 30, 45];
  const periods = ['AM', 'PM'];

  const formatTime = (timeObj) => {
    const hour = timeObj.hour.toString().padStart(2, '0');
    const minute = timeObj.minute.toString().padStart(2, '0');
    return `${hour}:${minute} ${timeObj.period}`;
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: THEME_COLORS.text.secondary }}>
        End Time
      </Typography>
      
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1, sm: 1.5 },
        p: { xs: 1, sm: 1.5 },
        border: `2px solid ${THEME_COLORS.primary.main}20`,
        borderRadius: THEME_RADIUS.medium,
        background: 'rgba(255, 255, 255, 0.9)',
        minHeight: { xs: 'auto', sm: 'auto' },
        '&:hover': {
          borderColor: THEME_COLORS.primary.main,
          background: 'rgba(255, 255, 255, 1)',
        }
      }}>
        {/* Time Selectors Container */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-end', 
          gap: { xs: 0.25, sm: 0.5 },
          justifyContent: 'center',
          flexWrap: 'nowrap',
          width: '100%'
        }}>
          {/* Hour Selector */}
          <Box sx={{ 
            flex: 1, 
            minWidth: { xs: 60, sm: 70 },
            maxWidth: { xs: 80, sm: 90 }
          }}>
            <Typography variant="caption" sx={{ 
              color: THEME_COLORS.text.secondary, 
              mb: 0.25, 
              display: 'block',
              fontSize: { xs: '0.65rem', sm: '0.7rem' },
              textAlign: 'center',
              fontWeight: 600
            }}>
              Hour
            </Typography>
            <Select
              value={endTime.hour}
              onChange={(e) => onEndTimeChange({ ...endTime, hour: e.target.value })}
              size="small"
              fullWidth
              sx={{
                '& .MuiSelect-select': {
                  textAlign: 'center',
                  fontWeight: 600,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  py: { xs: 0.5, sm: 0.5 }
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 200,
                    '& .MuiMenuItem-root': {
                      justifyContent: 'center',
                      minHeight: 40,
                    }
                  }
                }
              }}
            >
              {hours.map(hour => (
                <MenuItem key={hour} value={hour}>
                  {hour.toString().padStart(2, '0')}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Typography variant="h6" sx={{ 
            color: THEME_COLORS.text.secondary, 
            mx: { xs: 0.1, sm: 0.25 },
            alignSelf: 'flex-end',
            mb: 0.5,
            fontSize: { xs: '1.1rem', sm: '1.3rem' },
            fontWeight: 300
          }}>
            :
          </Typography>

          {/* Minute Selector */}
          <Box sx={{ 
            flex: 1, 
            minWidth: { xs: 60, sm: 70 },
            maxWidth: { xs: 80, sm: 90 }
          }}>
            <Typography variant="caption" sx={{ 
              color: THEME_COLORS.text.secondary, 
              mb: 0.25, 
              display: 'block',
              fontSize: { xs: '0.65rem', sm: '0.7rem' },
              textAlign: 'center',
              fontWeight: 600
            }}>
              Minute
            </Typography>
            <Select
              value={endTime.minute}
              onChange={(e) => onEndTimeChange({ ...endTime, minute: e.target.value })}
              size="small"
              fullWidth
              sx={{
                '& .MuiSelect-select': {
                  textAlign: 'center',
                  fontWeight: 600,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  py: { xs: 1, sm: 0.5 }
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 200,
                    '& .MuiMenuItem-root': {
                      justifyContent: 'center',
                      minHeight: 40,
                    }
                  }
                }
              }}
            >
              {minutes.map(minute => (
                <MenuItem key={minute} value={minute}>
                  {minute.toString().padStart(2, '0')}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Period Selector */}
          <Box sx={{ 
            flex: 1, 
            minWidth: { xs: 60, sm: 70 },
            maxWidth: { xs: 80, sm: 90 }
          }}>
            <Typography variant="caption" sx={{ 
              color: THEME_COLORS.text.secondary, 
              mb: 0.25, 
              display: 'block',
              fontSize: { xs: '0.65rem', sm: '0.7rem' },
              textAlign: 'center',
              fontWeight: 600
            }}>
              Period
            </Typography>
            <Select
              value={endTime.period}
              onChange={(e) => onEndTimeChange({ ...endTime, period: e.target.value })}
              size="small"
              fullWidth
              sx={{
                '& .MuiSelect-select': {
                  textAlign: 'center',
                  fontWeight: 600,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  py: { xs: 1, sm: 0.5 }
                }
              }}
            >
              {periods.map(period => (
                <MenuItem key={period} value={period}>
                  {period}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* Time Display */}
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 0.5, sm: 1 },
          mt: { xs: 0.5, sm: 0.5 }
        }}>
          <Box sx={{ 
            p: { xs: 1, sm: 1.25 }, 
            background: THEME_GRADIENTS.primary,
            borderRadius: THEME_RADIUS.small,
            minWidth: { xs: 100, sm: 100 },
            textAlign: 'center',
            boxShadow: THEME_SHADOWS.small,
            flex: 1,
            maxWidth: { xs: 140, sm: 120 }
          }}>
            <Typography variant="body2" sx={{ 
              color: 'white', 
              fontWeight: 600,
              fontSize: { xs: '0.9rem', sm: '0.875rem' }
            }}>
              {formatTime(endTime)}
            </Typography>
          </Box>

          <IconButton
            size="small"
            sx={{ 
              color: THEME_COLORS.text.secondary,
              p: { xs: 0.5, sm: 1 },
              '&:hover': {
                color: THEME_COLORS.primary.main,
              }
            }}
          >
            <AccessTimeIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
});

const DateRangePicker = ({
  startDateTime,
  endDateTime,
  onStartDateTimeChange,
  onEndDateTimeChange,
  label = "Date & Time",
  required = false,
  error = false,
  helperText = '',
  showTimeSelection = true,
  minDate = null,
  maxDate = null,
  singleDayMode = false, // New prop for single-day activities
  sx = {},
  ...props
}) => {
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [enableTimeSelection, setEnableTimeSelection] = useState(showTimeSelection);
  
  // Separate time state - default to all-day (12:00 AM to 11:45 PM)
  const [startTime, setStartTime] = useState({ hour: 12, minute: 0, period: 'AM' });
  const [endTime, setEndTime] = useState({ hour: 11, minute: 45, period: 'PM' });
  
  const isUserSelectingRef = useRef(false);
  const isSyncingFromPropsRef = useRef(false); // Add ref to track when syncing from props
  const isAutoSetRef = useRef(false); // Add ref to track when auto-setting end time
  const timeoutRef = useRef(null); // Add ref for timeout cleanup
  const updateTimeoutRef = useRef(null); // Add ref for update debouncing
  
  // Add refs to track previous values to prevent unnecessary updates
  const prevValuesRef = useRef({
    startDate: null,
    endDate: null,
    startTime: null,
    endTime: null,
    enableTimeSelection: false
  });



  // Sync with parent props - but don't override user selection in progress
  useEffect(() => {
    
    // Skip sync if we're currently syncing from props to prevent loops
    if (isSyncingFromPropsRef.current) {
      return;
    }
    
    // Only sync if we're not currently in a user selection process
    // or if the prop values are significantly different from current state
    const shouldSync = !isUserSelectingRef.current || 
      (endDateTime && endDate && Math.abs(endDateTime.getTime() - endDate.getTime()) > 60000) ||
      (startDateTime && startDate && Math.abs(startDateTime.getTime() - startDate.getTime()) > 60000);
    
    if (shouldSync) {
      // Set flag to prevent onEndDateTimeChange from being called during prop sync
      isSyncingFromPropsRef.current = true;
      
      // Check if this looks like an auto-set scenario (end time is exactly 1 hour after start time)
      if (startDateTime && endDateTime) {
        const timeDiff = endDateTime.getTime() - startDateTime.getTime();
        const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
        const isAutoSet = Math.abs(timeDiff - oneHour) < 60000; // Within 1 minute of 1 hour
        isAutoSetRef.current = isAutoSet;
      } else {
        isAutoSetRef.current = false;
      }
      
      // Defensive date parsing
      const parsedStartDate = startDateTime ? (startDateTime instanceof Date ? startDateTime : new Date(startDateTime)) : null;
      const parsedEndDate = endDateTime ? (endDateTime instanceof Date ? endDateTime : new Date(endDateTime)) : null;
      
      // Validate dates
      const validStartDate = parsedStartDate && !isNaN(parsedStartDate.getTime()) ? parsedStartDate : null;
      const validEndDate = parsedEndDate && !isNaN(parsedEndDate.getTime()) ? parsedEndDate : null;
      
      
      setDateRange([validStartDate, validEndDate]);
      
      
      // Extract times from incoming dates if they have time information - inline to avoid dependency
      if (validStartDate) {
        let hour = validStartDate.getHours();
        let minute = validStartDate.getMinutes();
        const period = hour >= 12 ? 'PM' : 'AM';
        
        // Round minutes to nearest valid option (0, 15, 30, 45)
        const validMinutes = [0, 15, 30, 45];
        minute = validMinutes.reduce((prev, curr) => 
          Math.abs(curr - minute) < Math.abs(prev - minute) ? curr : prev
        );
        
        // Convert to 12-hour format
        let hour12 = hour;
        if (hour === 0) {
          hour12 = 12;
        } else if (hour > 12) {
          hour12 = hour - 12;
        }
        
        const newStartTime = { hour: hour12, minute, period };
        setStartTime(newStartTime);
      }
      if (validEndDate) {
        let hour = validEndDate.getHours();
        let minute = validEndDate.getMinutes();
        const period = hour >= 12 ? 'PM' : 'AM';
        
        
        // Round minutes to nearest valid option (0, 15, 30, 45)
        const validMinutes = [0, 15, 30, 45];
        minute = validMinutes.reduce((prev, curr) => 
          Math.abs(curr - minute) < Math.abs(prev - minute) ? curr : prev
        );
        
        // Convert to 12-hour format
        let hour12 = hour;
        if (hour === 0) {
          hour12 = 12;
        } else if (hour > 12) {
          hour12 = hour - 12;
        }
        
        const newEndTime = { hour: hour12, minute, period };
        setEndTime(newEndTime);
      }
      
      // Always enable time selection since we removed the toggle
      setEnableTimeSelection(true);
      
      // Reset the syncing flag after a short delay
      setTimeout(() => {
        isSyncingFromPropsRef.current = false;
        isAutoSetRef.current = false;
      }, 50);
    }
  }, [startDateTime, endDateTime, startDate, endDate]);


  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }
    };
  }, []);

  // Handle date range change with proper time handling
  const handleDateRangeChange = useCallback((update) => {
    isUserSelectingRef.current = true;
    
    if (Array.isArray(update)) {
      // Validate dates are within trip bounds
      const [newStartDate, newEndDate] = update;
      
      // Check if dates are within the allowed range (only if minDate/maxDate are provided)
      if (minDate && newStartDate && newStartDate < minDate) {
        console.warn('Start date is before trip start date');
        return;
      }
      if (maxDate && newEndDate && newEndDate > maxDate) {
        console.warn('End date is after trip end date');
        return;
      }
      if (minDate && newEndDate && newEndDate < minDate) {
        console.warn('End date is before trip start date');
        return;
      }
      if (maxDate && newStartDate && newStartDate > maxDate) {
        console.warn('Start date is after trip end date');
        return;
      }
      
      setDateRange(update);
      
      // Call parent callbacks to update form data
      if (onStartDateTimeChange) {
        onStartDateTimeChange(newStartDate);
      }
      if (onEndDateTimeChange) {
        onEndDateTimeChange(newEndDate);
      }
    }
  }, [minDate, maxDate, onStartDateTimeChange, onEndDateTimeChange]);

  // Reset user selecting flag when date range is updated
  useEffect(() => {
    if (isUserSelectingRef.current && dateRange && dateRange.length === 2) {
      // Reset the flag after the state has been updated
      const timeoutId = setTimeout(() => {
        isUserSelectingRef.current = false;
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [dateRange]);

  // Handle single date change for single-day activities
  const handleSingleDateChange = useCallback((date) => {
    isUserSelectingRef.current = true;
    
    if (date) {
      // Validate date is within trip bounds
      if (minDate && date < minDate) {
        console.warn('Selected date is before trip start date');
        return;
      }
      if (maxDate && date > maxDate) {
        console.warn('Selected date is after trip end date');
        return;
      }
      
      setDateRange([date, null]);
    } else {
      setDateRange([null, null]);
    }

    // Clear existing timeout before setting new one
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Reset the flag after a delay with cleanup
    timeoutRef.current = setTimeout(() => {
      isUserSelectingRef.current = false;
      timeoutRef.current = null;
    }, 500);
  }, [minDate, maxDate]);

  // Handle clear dates
  const handleClear = useCallback(() => {
    setDateRange([null, null]);
    if (onStartDateTimeChange) onStartDateTimeChange(null);
    if (onEndDateTimeChange) onEndDateTimeChange(null);
  }, [onStartDateTimeChange, onEndDateTimeChange]);


  // Handle time changes with user selection flag
  const handleStartTimeChange = useCallback((newTime) => {
    isUserSelectingRef.current = true;
    setStartTime(newTime);
    
    // Immediately trigger parent callback for time changes to prevent loss
    if (startDate && enableTimeSelection && onStartDateTimeChange) {
      const combined = new Date(startDate);
      let hour = newTime.hour;
      
      // Convert to 24-hour format
      if (newTime.period === 'PM' && hour !== 12) {
        hour += 12;
      } else if (newTime.period === 'AM' && hour === 12) {
        hour = 0;
      }
      
      combined.setHours(hour, newTime.minute, 0, 0);
      onStartDateTimeChange(combined);
    }
    
    // Reset the flag after a delay - shorter timeout to allow prop sync
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      isUserSelectingRef.current = false;
      timeoutRef.current = null;
    }, 100);
  }, [startDate, enableTimeSelection, onStartDateTimeChange]);

  const handleEndTimeChange = useCallback((newTime) => {
    isUserSelectingRef.current = true;
    setEndTime(newTime);
    
    // Only trigger parent callback if this is a user-initiated change, not prop sync
    // But allow callbacks during auto-set scenarios
    const shouldCallParent = enableTimeSelection && onEndDateTimeChange && (!isSyncingFromPropsRef.current || isAutoSetRef.current);
    if (shouldCallParent) {
      const combined = new Date(endDate || startDate);
      let hour = newTime.hour;
      
      // Convert to 24-hour format
      if (newTime.period === 'PM' && hour !== 12) {
        hour += 12;
      } else if (newTime.period === 'AM' && hour === 12) {
        hour = 0;
      }
      
      combined.setHours(hour, newTime.minute, 0, 0);
      onEndDateTimeChange(combined);
    }
    
    // Reset the flag after a delay - shorter timeout to allow prop sync
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      isUserSelectingRef.current = false;
      timeoutRef.current = null;
    }, 100);
  }, [endDate, startDate, enableTimeSelection, onEndDateTimeChange]);

  // Check if it's a single day event - memoized to prevent recalculation
  const isSingleDay = useMemo(() => {
    return startDate && endDate ? isSameDay(startDate, endDate) : false;
  }, [startDate, endDate]);

  // Format duration - memoized to prevent recalculation
  const formattedDuration = useMemo(() => {
    if (!startDate || !endDate) return '';
    
    const diffMs = endDate.getTime() - startDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    // Simplified duration calculation to prevent toggling
    if (isSingleDay) {
      if (enableTimeSelection) {
        const diffHours = Math.round(diffMs / (1000 * 60 * 60) * 10) / 10;
        if (diffHours < 1) {
          const diffMinutes = Math.round(diffMs / (1000 * 60));
          return `${diffMinutes} minutes`;
        }
        return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
      } else {
        return 'All day';
      }
    } else {
      return `${diffDays + 1} day${diffDays !== 0 ? 's' : ''}`;
    }
  }, [startDate, endDate, isSingleDay, enableTimeSelection]);

  // Memoized display value to prevent recalculation
  const displayValue = useMemo(() => {
    // Always show the date range in the input field, regardless of time selection mode
    if (!startDate) return '';
    
    if (!endDate) {
      // Only start date selected
      return format(startDate, 'MMM d, yyyy');
    }
    
    // Both dates selected
    const startFormat = format(startDate, 'MMM d, yyyy');
    const endFormat = format(endDate, 'MMM d, yyyy');
    
    return `${startFormat} - ${endFormat}`;
  }, [startDate, endDate]);






  // Custom input component for range picker
  const RangeInput = React.memo(React.forwardRef(({ value, onClick, onChange, placeholder }, ref) => {
    return (
      <TextField
        ref={ref}
        fullWidth
        value={displayValue}
        onClick={onClick}
        onChange={onChange}
        placeholder={placeholder || "Click to select date range..."}
        label={label}
        required={required}
        error={error}
        helperText={helperText}
        InputProps={{
          readOnly: true,
          style: { cursor: 'pointer' },
          startAdornment: (
            <InputAdornment position="start">
              <CalendarIcon sx={{ color: THEME_COLORS.primary.main }} />
            </InputAdornment>
          ),
          endAdornment: (startDate || endDate) ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                sx={{
                  color: THEME_COLORS.text.secondary,
                  '&:hover': {
                    color: THEME_COLORS.status.error,
                  }
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: THEME_RADIUS.small,
            cursor: 'pointer',
            width: '100%',
            minWidth: { xs: '100%', sm: '400px' },
            '& .MuiInputBase-input': {
              fontSize: '1rem',
              fontWeight: 500,
              cursor: 'pointer',
            },
            '&:hover fieldset': {
              borderColor: THEME_COLORS.primary.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: THEME_COLORS.primary.main,
            }
          },
          '& .MuiInputLabel-root': {
            fontSize: '1rem',
            fontWeight: 600,
            '&.Mui-focused': {
              color: THEME_COLORS.primary.main,
            }
          }
        }}
      />
    );
  }));

  // Memoized popper props to prevent object recreation
  const popperProps = useMemo(() => ({
    strategy: 'fixed',
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          boundary: 'viewport',
          padding: 16,
          altBoundary: true,
        }
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['bottom-start', 'bottom-end', 'top-start', 'top-end'],
        }
      },
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        }
      }
    ]
  }), []);

  // Update parent when dates or times change - avoid infinite loop
  useEffect(() => {
    const prev = prevValuesRef.current;
    
    // Check if any values actually changed
    const hasChanged = 
      startDate !== prev.startDate ||
      endDate !== prev.endDate ||
      JSON.stringify(startTime) !== JSON.stringify(prev.startTime) ||
      JSON.stringify(endTime) !== JSON.stringify(prev.endTime) ||
      enableTimeSelection !== prev.enableTimeSelection;
    
    if (!hasChanged) return;
    
    // CRITICAL: Only update parent if this is a user-initiated change
    // Do NOT update if we're currently syncing from parent props
    if (isUserSelectingRef.current && !isSyncingFromPropsRef.current) {
      
      // Update previous values
      prevValuesRef.current = {
        startDate,
        endDate,
        startTime: { ...startTime },
        endTime: { ...endTime },
        enableTimeSelection
      };
      
      // Clear existing update timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      
      // Debounce the update to prevent rapid-fire changes
      updateTimeoutRef.current = setTimeout(() => {
        if (enableTimeSelection) {
          // Use user selected times directly without timezone conversion
          const startDateTime = startDate ? (() => {
            const combined = new Date(startDate);
            let hour = startTime.hour;
            
            // Convert to 24-hour format
            if (startTime.period === 'PM' && hour !== 12) {
              hour += 12;
            } else if (startTime.period === 'AM' && hour === 12) {
              hour = 0;
            }
            
            // Set time directly as user selected - no timezone conversion
            combined.setHours(hour, startTime.minute, 0, 0);
            
            
            return combined;
          })() : null;
          
          const endDateTime = (endDate || startDate) ? (() => {
            const combined = new Date(endDate || startDate);
            let hour = endTime.hour;
            
            // Convert to 24-hour format
            if (endTime.period === 'PM' && hour !== 12) {
              hour += 12;
            } else if (endTime.period === 'AM' && hour === 12) {
              hour = 0;
            }
            
            // Set time directly as user selected - no timezone conversion
            combined.setHours(hour, endTime.minute, 0, 0);
            
            
            return combined;
          })() : null;
          
          if (onStartDateTimeChange) onStartDateTimeChange(startDateTime);
          if (onEndDateTimeChange) onEndDateTimeChange(endDateTime);
        } else {
          // All Day Event - set 12:00 AM - 11:45 PM
          const allDayStart = startDate ? (() => {
            const combined = new Date(startDate);
            combined.setHours(0, 0, 0, 0); // 12:00 AM
            return combined;
          })() : null;
          
          const allDayEnd = (endDate || startDate) ? (() => {
            const combined = new Date(endDate || startDate);
            combined.setHours(23, 45, 0, 0); // 11:45 PM (since 59 minutes not available)
            return combined;
          })() : null;
          
          if (onStartDateTimeChange) onStartDateTimeChange(allDayStart);
          if (onEndDateTimeChange) onEndDateTimeChange(allDayEnd);
        }
        updateTimeoutRef.current = null;
              }, 50); // Reduced to 50ms for faster updates
      } else {
        // Just update our tracking without calling parent callbacks
      prevValuesRef.current = {
        startDate,
        endDate,
        startTime: { ...startTime },
        endTime: { ...endTime },
        enableTimeSelection
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, startTime, endTime, enableTimeSelection]);

  return (
    <Box sx={{ 
      ...sx,
      '& .react-datepicker-popper': {
        zIndex: 9999,
        '& .react-datepicker': {
          fontFamily: 'inherit',
          fontSize: '1rem',
          borderRadius: THEME_RADIUS.small,
          border: `1px solid ${THEME_COLORS.primary.main}`,
          boxShadow: THEME_SHADOWS.heavy,
          '& .react-datepicker__header': {
            backgroundColor: THEME_COLORS.primary.main,
            borderTopLeftRadius: THEME_RADIUS.small,
            borderTopRightRadius: THEME_RADIUS.small,
            borderBottom: 'none',
            color: 'white',
          },
          '& .react-datepicker__current-month': {
            color: 'white',
            fontWeight: 600,
          },
          '& .react-datepicker__day-name': {
            color: 'white',
            fontWeight: 600,
          },
          '& .react-datepicker__day--selected': {
            backgroundColor: THEME_COLORS.accent.main,
            '&:hover': {
              backgroundColor: THEME_COLORS.accent.dark,
            }
          },
          '& .react-datepicker__day--in-selecting-range': {
            backgroundColor: `${THEME_COLORS.primary.main}40`,
          },
          '& .react-datepicker__day--in-range': {
            backgroundColor: `${THEME_COLORS.primary.main}20`,
          },
          '& .react-datepicker__day--excluded': {
            backgroundColor: '#f5f5f5',
            color: '#ccc',
            cursor: 'not-allowed',
            '&:hover': {
              backgroundColor: '#f5f5f5',
              color: '#ccc',
            }
          },
          '& .react-datepicker__day--highlighted-trip-range': {
            backgroundColor: `${THEME_COLORS.accent.main}10`,
            border: `1px solid ${THEME_COLORS.accent.main}40`,
          },
          '& .react-datepicker__time-container': {
            borderLeft: `1px solid ${THEME_COLORS.primary.main}`,
            width: '120px',
          },
          '& .react-datepicker__time': {
            background: 'white',
            borderBottomRightRadius: THEME_RADIUS.small,
          },
          '& .react-datepicker__time-box': {
            width: '120px',
          },
          '& .react-datepicker__time-list': {
            height: '200px !important',
            overflow: 'auto',
          },
          '& .react-datepicker__time-list-item': {
            height: '30px',
            padding: '5px 10px',
            '&:hover': {
              backgroundColor: `${THEME_COLORS.primary.main}20`,
            },
            '&--selected': {
              backgroundColor: THEME_COLORS.primary.main,
              color: 'white',
              fontWeight: 600,
            }
          }
        }
      }
    }}>
      {/* Header */}
      <Typography 
        variant="subtitle2" 
        sx={{ 
          mb: 2, 
          fontWeight: 700, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5,
          color: THEME_COLORS.text.primary,
          fontSize: '1rem',
        }}
      >
        <CalendarIcon sx={{ fontSize: 20, color: THEME_COLORS.primary.main }} />
        {label}
        {required && <span style={{ color: THEME_COLORS.status.error }}>*</span>}
      </Typography>


      {/* Date Range Picker - Separated Date and Time */}
      <Box sx={{ mb: 3 }}>
        {/* Date Selection */}
        <Box sx={{ mb: enableTimeSelection ? 3 : 0 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: THEME_COLORS.text.primary }}>
            {singleDayMode ? 'Select Date' : 'Select Dates'}
          </Typography>
          <DatePicker
            selectsRange={!singleDayMode}
            startDate={singleDayMode ? startDate : startDate}
            endDate={singleDayMode ? null : endDate}
            selected={singleDayMode ? startDate : null}
            onChange={singleDayMode ? handleSingleDateChange : handleDateRangeChange}
            dateFormat="MMM d, yyyy"
            minDate={minDate}
            maxDate={maxDate}
            customInput={<RangeInput />}
            shouldCloseOnSelect={singleDayMode}
            popperPlacement="bottom-start"
            popperClassName="react-datepicker-popper"
            popperProps={popperProps}
            allowSameDay={true}
            isClearable={false}
            autoComplete="off"
            placeholderText={singleDayMode ? "Select date..." : "Select date range..."}
            excludeDateIntervals={[
              // Exclude dates before trip start
              ...(minDate ? [{ start: new Date(1900, 0, 1), end: new Date(minDate.getTime() - 24 * 60 * 60 * 1000) }] : []),
              // Exclude dates after trip end
              ...(maxDate ? [{ start: new Date(maxDate.getTime() + 24 * 60 * 60 * 1000), end: new Date(2100, 11, 31) }] : [])
            ]}
            highlightDates={[
              // Highlight the trip duration range
              ...(minDate && maxDate ? [{
                "react-datepicker__day--highlighted-trip-range": [minDate, maxDate]
              }] : [])
            ]}
          />
        </Box>

        {/* Time Selection - Only show when enabled and dates are selected */}
        {enableTimeSelection && startDate && (
          <Box sx={{ 
            p: { xs: 2, sm: 3 }, 
            border: `2px solid ${THEME_COLORS.primary.main}20`,
            borderRadius: THEME_RADIUS.medium,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: { xs: 2, sm: 3 }, 
                fontWeight: 700, 
                color: THEME_COLORS.text.primary,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }}
            >
              <AccessTimeIcon sx={{ 
                color: THEME_COLORS.primary.main,
                fontSize: { xs: 20, sm: 24 }
              }} />
              Set Specific Times
            </Typography>

            {/* Separate Time Picker Panels */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'column', lg: 'row' },
              gap: { xs: 2, sm: 3, lg: 4 },
              alignItems: 'stretch',
              width: '100%',
              '@media (max-width: 899px)': {
                flexDirection: 'column !important'
              }
            }}>
              {startDate && (
                <Box sx={{ 
                  flex: { xs: 'none', lg: 1 },
                  width: { xs: '100%', lg: 'auto' },
                  minWidth: 0,
                  '@media (max-width: 899px)': {
                    width: '100% !important',
                    flex: 'none !important'
                  }
                }}>
                  <StartTimePanel 
                    startTime={startTime}
                    onStartTimeChange={handleStartTimeChange}
                  />
                </Box>
              )}
              
              <Box sx={{ 
                flex: { xs: 'none', lg: 1 },
                width: { xs: '100%', lg: 'auto' },
                minWidth: 0,
                '@media (max-width: 899px)': {
                  width: '100% !important',
                  flex: 'none !important'
                }
              }}>
                <EndTimePanel 
                  endTime={endTime}
                  onEndTimeChange={handleEndTimeChange}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Duration Display - Simplified to prevent toggling */}
      {startDate && endDate && formattedDuration && (
        <Box sx={{ 
          mb: 2, 
          p: 2, 
          borderRadius: THEME_RADIUS.small,
          background: THEME_GRADIENTS.accent,
          color: 'white',
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5,
        }}>
          <TimeIcon sx={{ fontSize: 18 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Duration: {formattedDuration}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {isSingleDay ? 'Single day event' : 'Multi-day trip'}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Helper Text */}
      {helperText && (
        <Typography 
          variant="body2" 
          sx={{ 
            mt: 1,
            color: error ? THEME_COLORS.status.error : THEME_COLORS.text.secondary,
            fontSize: '0.875rem',
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default DateRangePicker;